import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtDate(d: string | Date | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
const num = (s: string) => Number((s || "").replace(",", "."));

// ─── Ürün Dönüşümü: kaynak lot → hedef ürün (kaynak tüketilir, hedef lotu üretilir) ───

export interface ConvertSource {
  id: string; code: string;
  itemKind: "stock" | "semi" | "finished"; itemId: string;
  itemCode: string; itemName: string;
  qtyOnHand: number; uom: string;
}

export function ConversionDrawer({ open, source, onClose, onDone }: {
  open: boolean; source: ConvertSource | null; onClose: () => void; onDone?: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [targetSel, setTargetSel] = useState("");
  const [sourceQty, setSourceQty] = useState("");
  const [targetQty, setTargetQty] = useState("");
  const [consumeRecipe, setConsumeRecipe] = useState(true);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const produciblesQ = trpc.recipe.producibles.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const convertMut = trpc.productionBatch.convert.useMutation();

  // Seçili hedefin reçetesindeki "dönüşümde tükenir" işaretli kalemler (onay + önizleme).
  const [targetKindSel, targetIdSel] = targetSel ? (targetSel.split(":") as ["semi" | "finished", string]) : [null, null];
  const convItemsQ = trpc.recipe.conversionItemsFor.useQuery(
    { kind: (targetKindSel ?? "finished"), id: targetIdSel ?? "" },
    { enabled: open && !!targetIdSel, staleTime: 60_000 },
  );

  useEffect(() => {
    if (!open) return;
    setTargetSel(""); setSourceQty(source ? String(source.qtyOnHand) : ""); setTargetQty(""); setConsumeRecipe(true); setNotes(""); setErr(null);
    convertMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.id]);

  const targetOptions = useMemo<SSOption[]>(
    () => ((produciblesQ.data ?? []) as Array<{ kind: string; id: string; code: string; name: string; baseUomSymbol: string | null }>)
      .filter((p) => !(source && p.kind === source.itemKind && p.id === source.itemId))
      .map((p) => ({ value: `${p.kind}:${p.id}`, label: p.name, tail: p.code })),
    [produciblesQ.data, source],
  );
  const targetUom = useMemo(
    () => (produciblesQ.data ?? []).find((x) => `${x.kind}:${x.id}` === targetSel)?.baseUomSymbol ?? "",
    [produciblesQ.data, targetSel],
  );

  const sq = num(sourceQty), tq = num(targetQty);
  const loss = sq > 0 && tq >= 0 ? Math.max(0, sq - tq) : null;
  const mutErr = convertMut.error?.message ?? null;

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!source) return;
    if (!targetSel) return setErr("Hedef ürün seçin.");
    if (!(sq > 0)) return setErr("Tüketilecek (kaynak) miktar 0'dan büyük olmalı.");
    if (sq > source.qtyOnHand + 1e-6) return setErr(`Kaynak lotta yeterli miktar yok (mevcut ${fmtQty(source.qtyOnHand)} ${source.uom}).`);
    if (!(tq > 0)) return setErr("Üretilecek (hedef) miktar 0'dan büyük olmalı.");
    const [k, id] = targetSel.split(":");
    try {
      const res = await convertMut.mutateAsync({
        sourceLotId: source.id, targetKind: k as "semi" | "finished", targetItemId: id!,
        sourceQty: sq, targetQty: tq, consumeRecipeItems: consumeRecipe, notes: notes.trim() || undefined,
      });
      await qc.invalidateQueries();
      const warn = res.warnings && res.warnings.length ? ` · ${res.warnings.length} stok uyarısı` : "";
      onDone?.(`Dönüşüm tamam · ${res.code} · yeni lot ${res.lotCode}${warn}`);
      onClose();
    } catch { /* mutErr banner */ }
  }

  return (
    <Drawer
      open={open} onClose={onClose} size="md" title="Ürün Dönüşümü"
      subtitle={source ? `Kaynak: ${source.code} · ${source.itemCode}` : undefined}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={convertMut.isPending}>İptal</button>
          <button type="submit" form="conv-form" className="btn-md primary" disabled={convertMut.isPending}>
            {convertMut.isPending ? "Dönüştürülüyor…" : "Dönüştür"}
          </button>
        </>
      }
    >
      <form id="conv-form" onSubmit={submit} noValidate>
        {err || mutErr ? <div className="form-banner-err" role="alert">{err ?? mutErr}</div> : null}

        {source ? (
          <div className="form-field">
            <span className="lbl">Kaynak Lot</span>
            <div style={{ padding: "10px 12px", border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)" }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{source.itemName}</div>
              <div style={{ fontSize: 12.5, color: "var(--neutral-600)", marginTop: 3 }}>
                <span className="mono">{source.code}</span> · Stok: <b className="mono" style={{ color: "var(--neutral-900)" }}>{fmtQty(source.qtyOnHand)} {source.uom}</b>
              </div>
            </div>
          </div>
        ) : null}

        <div className="form-field">
          <label className="lbl" htmlFor="conv-target">Hedef Ürün <span className="req">*</span></label>
          <SearchSelect
            id="conv-target" value={targetSel || null} options={targetOptions}
            onChange={(v) => setTargetSel(v ?? "")}
            placeholder="— Dönüştürülecek ürün —" searchPlaceholder="Ürün adı / kodu ara…" emptyText="Ürün bulunamadı"
          />
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="conv-src">Tüketilen (kaynak) <span className="req">*</span></label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input id="conv-src" className="form-input" type="text" value={sourceQty} onChange={(e) => setSourceQty(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" style={{ flex: 1 }} aria-label="Tüketilen kaynak miktar" />
              <span className="mono" style={{ fontSize: 13, color: "var(--neutral-600)", whiteSpace: "nowrap" }}>{source?.uom ?? ""}</span>
            </div>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="conv-tgt">Üretilen (hedef) <span className="req">*</span></label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input id="conv-tgt" className="form-input" type="text" value={targetQty} onChange={(e) => setTargetQty(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" style={{ flex: 1 }} aria-label="Üretilen hedef miktar" />
              <span className="mono" style={{ fontSize: 13, color: "var(--neutral-600)", whiteSpace: "nowrap" }}>{targetUom}</span>
            </div>
          </div>
        </div>
        {loss != null && loss > 0 ? (
          <div className="help" style={{ color: "var(--warning-text)" }}>Fire / kayıp: <b className="mono">{fmtQty(loss)} {source?.uom}</b> (tüketilen − üretilen).</div>
        ) : null}

        {/* Dönüşümde tüketilecek reçete kalemleri (onay + önizleme) */}
        {targetIdSel ? (
          <div className="form-field">
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={consumeRecipe} onChange={(e) => setConsumeRecipe(e.target.checked)} style={{ width: 15, height: 15, marginTop: 2, accentColor: "var(--primary)", cursor: "pointer", flexShrink: 0 }} aria-label="Reçete dönüşüm kalemlerini de tüket" />
              <span style={{ fontSize: 13, lineHeight: 1.35 }}>
                Reçetedeki "dönüşümde tükenir" kalemlerini de düş
                <span style={{ display: "block", fontSize: 11.5, color: "var(--neutral-500)" }}>
                  Dönüşümde fiziksel tüketilen ek kalemler (ambalaj, yarı mamul vb.) üretilen miktara göre stoktan düşülür.
                </span>
              </span>
            </label>
            {consumeRecipe && (convItemsQ.data?.length ?? 0) > 0 ? (
              <div style={{ marginTop: 8, border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", padding: "8px 10px" }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.02em", color: "var(--neutral-500)", marginBottom: 4 }}>
                  Düşülecek kalemler{tq > 0 ? " (tahmini)" : ""}
                </div>
                {convItemsQ.data!.map((it) => {
                  const need = tq > 0 ? Math.round(tq * it.ratio * 1e4) / 1e4 : null;
                  return (
                    <div key={`${it.itemKind}:${it.itemId}`} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "2px 0" }}>
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name} <span className="mono" style={{ color: "var(--neutral-400)", fontSize: 10.5 }}>{it.code}</span></span>
                      <span className="mono" style={{ color: "var(--neutral-800)", whiteSpace: "nowrap" }}>{need != null ? `${fmtQty(need)} ${it.uom}` : `— ${it.uom}`}</span>
                    </div>
                  );
                })}
              </div>
            ) : consumeRecipe && convItemsQ.isFetched && (convItemsQ.data?.length ?? 0) === 0 ? (
              <div className="help" style={{ marginTop: 4 }}>Bu ürünün reçetesinde "dönüşümde tükenir" işaretli kalem yok — yalnız kaynak lot tüketilir.</div>
            ) : null}
          </div>
        ) : null}

        <div className="form-field">
          <label className="lbl" htmlFor="conv-notes">Not</label>
          <textarea id="conv-notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={2} />
        </div>
        <div style={{ fontSize: 11.5, color: "var(--neutral-500)", marginTop: 6, lineHeight: 1.4 }}>
          Kaynak lot tüketilir, hedef üründen yeni lot üretilir. İzlenebilirlik: yeni lotun geçmişi bu kaynak lota (ve onun bileşenlerine) iner. Geri almak için oluşan üretim partisini silin.
        </div>
      </form>
    </Drawer>
  );
}

// ─── Siparişe Bağla: stoktaki (siparişsiz) partiyi bir siparişe atıf + earmark ───

export interface BindBatch {
  batchId: string;
  itemKind: "semi" | "finished"; itemId: string;
  itemCode: string; itemName: string;
  lotCode: string; qtyOnHand: number; uom: string;
}

export function BindOrderDrawer({ open, batch, onClose, onDone }: {
  open: boolean; batch: BindBatch | null; onClose: () => void; onDone?: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [sel, setSel] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const ordersQ = trpc.productionBatch.bindableOrders.useQuery(
    { itemKind: batch?.itemKind ?? "finished", itemId: batch?.itemId ?? "" },
    { enabled: open && !!batch },
  );
  const bindMut = trpc.productionBatch.bindToOrder.useMutation();

  useEffect(() => {
    if (!open) return;
    setSel(null); setErr(null); bindMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batch?.batchId]);

  const orders = ordersQ.data ?? [];
  const mutErr = bindMut.error?.message ?? null;

  async function submit() {
    setErr(null);
    if (!batch) return;
    if (!sel) return setErr("Bağlanacak siparişi seçin.");
    try {
      const res = await bindMut.mutateAsync({ batchId: batch.batchId, orderId: sel });
      await qc.invalidateQueries();
      onDone?.(`${res.code} siparişe bağlandı${res.reservedQty > 0 ? ` · ${fmtQty(res.reservedQty)} rezerve` : ""}${res.ready ? " · sipariş sevkiyata hazır" : ""}.`);
      onClose();
    } catch { /* mutErr banner */ }
  }

  return (
    <Drawer
      open={open} onClose={onClose} size="md" title="Siparişe Bağla"
      subtitle={batch ? `${batch.lotCode} · ${batch.itemCode}` : undefined}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={bindMut.isPending}>İptal</button>
          <button type="button" className="btn-md primary" onClick={submit} disabled={bindMut.isPending || !sel}>
            {bindMut.isPending ? "Bağlanıyor…" : "Bağla"}
          </button>
        </>
      }
    >
      {err || mutErr ? <div className="form-banner-err" role="alert">{err ?? mutErr}</div> : null}

      {batch ? (
        <div className="form-field">
          <span className="lbl">Stoktaki Parti</span>
          <div style={{ padding: "10px 12px", border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)" }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{batch.itemName}</div>
            <div style={{ fontSize: 12.5, color: "var(--neutral-600)", marginTop: 3 }}>
              <span className="mono">{batch.lotCode}</span> · Stok: <b className="mono" style={{ color: "var(--neutral-900)" }}>{fmtQty(batch.qtyOnHand)} {batch.uom}</b>
            </div>
          </div>
        </div>
      ) : null}

      <div className="form-field">
        <span className="lbl">Bu ürünü içeren siparişler (onaylı / üretimde)</span>
        {ordersQ.isLoading ? (
          <div className="help">Yükleniyor…</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: "12px 14px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12.5, color: "var(--neutral-500)", textAlign: "center" }}>
            Bu ürünü içeren onaylı/üretimde sipariş yok.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {orders.map((o) => {
              const active = sel === o.id;
              return (
                <button
                  type="button" key={o.id} onClick={() => setSel(o.id)}
                  aria-pressed={active}
                  style={{
                    textAlign: "left", padding: "10px 12px", borderRadius: "var(--radius-md)", cursor: "pointer",
                    border: active ? "2px solid var(--primary)" : "1px solid var(--neutral-200)",
                    background: active ? "var(--primary-subtle)" : "var(--neutral-50)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}><span className="mono">{o.code}</span> · {o.customerName}</span>
                    <span className="badge-pill" style={{ fontSize: 10.5 }}>{o.status === "confirmed" ? "Onaylı" : "Üretimde"}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--neutral-600)", marginTop: 3, display: "flex", gap: "4px 14px", flexWrap: "wrap" }}>
                    <span>Sipariş: <b className="mono">{fmtQty(o.ordered)}</b></span>
                    <span>Üretilen: <b className="mono">{fmtQty(o.produced)}</b></span>
                    <span>Kalan: <b className="mono" style={{ color: o.remaining > 0 ? "var(--warning-text)" : "var(--success-text)" }}>{fmtQty(o.remaining)}</b></span>
                    <span>Termin: <b className="mono">{fmtDate(o.teslimTarihi)}</b>{o.teslimTahmini ? <span title="Söz verilen teslim tarihi girilmemiş — istenen tarih gösteriliyor"> *</span> : null}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--neutral-500)", marginTop: 2, lineHeight: 1.4 }}>
        Parti seçilen siparişe atanır ve lot o siparişe rezerve edilir. İzlenebilirlik değişmez.
      </div>
    </Drawer>
  );
}
